import moment, { isMoment } from 'moment'
import {
  formatDateTick,
  addFormattedDate
} from 'helpers/advertisement/formatters'
import {
  sumKeys,
  maxKeys,
  avgKeys,
  avgRating,
  avgKeysCurrency
} from './objects'
import { filterArray } from './arrays'
import cloneDeep from 'lodash/cloneDeep'

export const formatDateAxis = val =>
  (isMoment(val) ? val : moment.utc(val)).format('YYYY-MM')

export const formatDateAxisMonthDay = (val, format) =>
  (isMoment(val) ? val : moment.utc(val)).format(format || 'MM-DD')

export const formatDateAxisMonthDayFull = (val, format) =>
  (isMoment(val) ? val : moment.utc(val, format)).format('MMM DD')

export const formatDateAxisMonthFull = (val, format) =>
  (isMoment(val) ? val : moment.utc(val, format)).format('MMM')

export const formatDateAxisRange = (startDate, endDate, format) => {
  return `${formatDateAxisMonthDayFull(
    startDate,
    format
  )} - ${formatDateAxisMonthDayFull(endDate, format)}`
}

export const getXAxisInterval = (dataLength, numOfSections) => {
  if (!dataLength || dataLength <= numOfSections) {
    return 0
  }

  return Math.round(dataLength / numOfSections)
}

export const groupDataByDay = (data, format) => {
  return data.reduce((accumulator, currentData) => {
    const date = moment.utc(currentData.date, format)
    const year = date.year()
    const month = date.month()
    const day = date.date()

    const key = `${year}-${month + 1}-${day}` // month + 1 because moment.js month starts from 0

    if (accumulator[key]) {
      accumulator[key].push(currentData)
    } else {
      accumulator[key] = [currentData]
    }

    return accumulator
  }, {})
}

export const groupDataByWeek = (data, format) => {
  const result = data.reduce((accumulator, currentData) => {
    const date = moment.utc(currentData.date, format)
    let year = date.year()
    const month = date.month()
    const week = date.week()

    let key = `${year}-${week}`

    // if month is 0 (i.e January) and week is bigger than 52 then we should group this with dates in the previous year
    // if month is 11 (Dec) and week is 1 then we should group this with dates in the new year
    if (month === 0 && week >= 52) {
      year -= 1
      key = `${year}-${week}`
    } else if (month === 11 && week === 1) {
      year += 1
      key = `${year}-${week}`
    }

    if (accumulator[key]) {
      accumulator[key].push(currentData)
    } else {
      accumulator[key] = [currentData]
    }

    return accumulator
  }, {})

  return result
}

/**
 * Deletes the first and last groupings if they are not full weeks
 */
export const groupDataByWeekAndTrim = (data, format) => {
  const groupedData = groupDataByWeek(data, format)
  const clonedData = cloneDeep(groupedData)

  const weeks = Object.keys(clonedData)
  const firstWeek = weeks[0]
  const lastWeek = weeks[weeks.length - 1]

  if (clonedData[firstWeek]?.length !== 7) delete clonedData[firstWeek]
  if (clonedData[lastWeek]?.length !== 7) delete clonedData[lastWeek]

  return clonedData
}

export const groupDataByMonth = (data, format) => {
  return data.reduce((accumulator, currentData) => {
    const date = moment.utc(currentData.date, format)
    const year = date.year()
    const month = date.month()

    const key = `${year}-${month}`

    if (accumulator[key]) {
      accumulator[key].push(currentData)
    } else {
      accumulator[key] = [currentData]
    }

    return accumulator
  }, {})
}

const groupDataByYear = (data, format) => {
  return data.reduce((accumulator, currentData) => {
    const year = moment(currentData.date, format).year()

    if (accumulator[year]) {
      accumulator[year].push(currentData)
    } else {
      accumulator[year] = [currentData]
    }

    return accumulator
  }, {})
}

export const getDateRange = ({
  data,
  interval,
  format,
  forcedInterval = undefined
}) => {
  const date = moment.utc(data[0].date, format)
  const endDate = moment.utc(data[data.length - 1].date, format)

  let intervalStartDate = date.clone()
  let intervalEndDate = endDate.clone()

  let isPartialData = false

  if (interval === 'weekly') {
    const startIsSunday = date.day() === 0 // Sunday
    const endIsSaturday = endDate.day() === 6 // Saturday

    if (!startIsSunday) intervalStartDate = date.clone().day(0)
    if (!endIsSaturday) intervalEndDate = date.clone().day(6)
    if (data.length < 7) isPartialData = true
  } else if (interval === 'monthly') {
    const startOfMonth = date.clone().startOf('month')
    const endOfMonth = endDate.clone().endOf('month')

    if (!date.isSame(startOfMonth)) intervalStartDate = startOfMonth
    if (!endDate.isSame(endOfMonth)) intervalEndDate = endOfMonth

    const daysInMonth = moment(date, format).daysInMonth()
    if (data.length < daysInMonth) isPartialData = true
  }

  return {
    date: date.format(format), // first date of the given data
    endDate: interval === 'daily' ? null : endDate.format(format), // last date of the given data, not needed for daily data
    formattedDate: formatDateTick(
      date,
      endDate,
      forcedInterval || interval,
      format
    ),
    intervalStartDate: intervalStartDate.format(format), // start date of the interval for given data, if data is monthly and in the 11th month, start date would be Nov 1st
    intervalEndDate: intervalEndDate.format(format), // end date of the interval for given data, if data is monthly and in the 11th month, end date would be Nov 30th
    isPartialData
  }
}
/**
 *
 * @param {Aarray} data
 * @param {string} interval - daily, weekly, monthly, yearly
 * @param {*} groupingType - sum, avg, max, avgCurrency
 * @param {Array} keys - keys to access from the incoming data and include in the returned grouped data
 * @param {boolean} groupDaily
 * @param {string} format - date format of the incoming data
 * @param {boolean} skipNullValues - skip null values in the sum
 * @returns {Array} - formatted data
 * [
 *    {
 *      date: string,
 *      formattedDate: string,
 *      endDate: string,
 *      isPartialData: boolean,
 *      {...grouped data based on groupingType and keys},
 *      rawGroupData: [...data]
 *    },
 *   ...
 * ]
 */
export const formatChartDataByInterval = (
  data,
  interval,
  groupingType,
  keys = [],
  groupDaily = false,
  format = undefined,
  skipNullValues = false
) => {
  if (!data || data.length === 0) return []

  let groupedData = {}

  if (['daily', 'day'].includes(interval) && groupDaily) {
    groupedData = groupDataByDay(data, format)
  } else if (['daily', 'day'].includes(interval)) {
    return addFormattedDate(data, interval)
  }

  if (['weekly', 'week'].includes(interval)) {
    groupedData = groupDataByWeek(data, format)
  } else if (['monthly', 'month'].includes(interval)) {
    groupedData = groupDataByMonth(data, format)
  } else if (['yearly', 'year'].includes(interval)) {
    groupedData = groupDataByYear(data, format)
  }

  if (groupingType) {
    let localKeys = keys

    if (!localKeys.length) {
      localKeys = Object.keys(data[0]).filter(key => key !== 'date')
    }

    return Object.values(groupedData).map(group => {
      const dateRange = getDateRange({ data: group, interval, format })

      let mergedGroup = {}

      switch (groupingType) {
        case 'avg':
          mergedGroup = avgKeys(group, localKeys, skipNullValues)
          break
        case 'avgCurrency':
          mergedGroup = avgKeysCurrency(group, localKeys)
          break
        case 'max':
          mergedGroup = maxKeys(group, localKeys)
          break
        default:
          mergedGroup = sumKeys(group, localKeys)
      }

      mergedGroup.rawGroupData = group

      return {
        ...dateRange,
        ...mergedGroup
      }
    })
  }

  const objKeys = Object.keys(
    data.reduce((accumulator, current) => ({ ...accumulator, ...current }), {})
  )

  const keysToGetSum = filterArray(objKeys, [
    'unitSales',
    'revenue',
    'revenue1p',
    'revenue3p',
    'revenueTotal',
    'unitSales3p',
    'unitSales1p',
    'unitSalesTotal',
    'oneToThree',
    'fourToTen',
    'elevenToTwenty',
    'twentyOneToOneHundred',
    'buyBoxSeller'
  ])

  const keysToGetMax = filterArray(objKeys, ['brands', 'asins'])
  const keysToGetAvg = filterArray(objKeys, [
    'avgReviews',
    'reviewCount',
    'newOfferCount',
    'usedOfferCount',
    'salesRank'
  ])
  const keysToGetAvgRating = filterArray(objKeys, ['starRating'])
  const keysToGetAvgCurrency = filterArray(objKeys, [
    'avgPrice',
    'usedPrice',
    'buyBoxPrice',
    'amazonPrice'
  ])

  return Object.values(groupedData).map(group => {
    const dateRange = getDateRange({ data: group, interval, format })

    const sumGroup = sumKeys(group, keysToGetSum)
    const maxGroup = maxKeys(group, keysToGetMax)
    const avgGroup = avgKeys(group, keysToGetAvg)
    const avgRatingGroup = avgRating(group, keysToGetAvgRating)
    const avgCurrencyGroup = avgKeysCurrency(group, keysToGetAvgCurrency)

    return {
      ...dateRange,
      ...sumGroup,
      ...maxGroup,
      ...avgGroup,
      ...avgRatingGroup,
      ...avgCurrencyGroup
    }
  })
}
